function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

export const ELITE_BATCH22_PART8: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ─── 89. Quiz Result Follow-Up | Attentive | Pattern I ─────────────────

  "Quiz Result Follow-Up": {
    nodes: [
      trigger("Attentive: Quiz completion webhook", "webhook_app", {
        source: "attentive",
        event: "quiz.completed",
        howItStarts:
          "Fires when a subscriber completes an on-site product quiz via Attentive and results are ready for personalized follow-up.",
        setup:
          "Connect Attentive quiz integration. Configure webhook to fire on quiz.completed events with subscriber phone, quiz ID, and raw answers payload.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract quiz answers, recommended products, and subscriber profile",
        280,
        "You are a quiz result analyst for an e-commerce brand. Parse the raw quiz payload and extract: subscriber_phone, quiz_name, answer_summary (concise string of key answers), recommended_products (array of up to 3 product objects with name, url, price), result_category (e.g. Dry Skin, Athletic Build), and confidence_score (0-1).",
        "Parse this quiz completion payload and extract structured results: {webhook_payload}",
        [
          { field: "subscriber_phone", as: "subscriber_phone", type: "string" },
          { field: "quiz_name", as: "quiz_name", type: "string" },
          { field: "answer_summary", as: "answer_summary", type: "string" },
          {
            field: "recommended_products",
            as: "recommended_products",
            type: "object",
          },
          { field: "result_category", as: "result_category", type: "string" },
          { field: "confidence_score", as: "confidence_score", type: "number" },
        ],
      ),
      n("3", "hitApi", "Attentive: Lookup subscriber engagement history", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.attentivemobile.com/v1/subscribers/{subscriber_phone}/attributes",
          headers: [
            { key: "Authorization", value: "Bearer {attentive_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "attributes.totalOrders",
              customName: "total_orders",
              type: "number",
            },
            {
              name: "attributes.lastPurchaseDate",
              customName: "last_purchase_date",
              type: "string",
            },
            {
              name: "attributes.engagementLevel",
              customName: "engagement_level",
              type: "string",
            },
          ],
        },
      }),
      n(
        "4",
        "condition",
        "Check: High confidence result (score >= 0.7)?",
        760,
        {
          conditionDetails: {
            conditions: [
              { variable: "confidence_score", operator: ">=", value: "0.7" },
            ],
          },
        },
      ),
      smsNode(
        "5",
        "Attentive: Send personalized quiz result SMS with top product",
        1000,
        "{subscriber_phone}",
        "Hey! Your {quiz_name} results are in -- you're a {result_category}! We picked these just for you: {recommended_products[0].name} ({recommended_products[0].price}). Shop your match: {recommended_products[0].url}",
      ),
      smsNode(
        "5b",
        "Attentive: Send general quiz result SMS with category link",
        1000,
        "{subscriber_phone}",
        "Thanks for taking our {quiz_name}! Based on your answers, we think you'd love our {result_category} collection. Browse picks for you: https://shop.example.com/collections/{result_category}",
        400,
      ),
      n(
        "6",
        "delayAction",
        "Wait 3 days for browsing and purchase activity",
        1240,
        {
          delayDetails: { duration: 3, unit: "days" },
        },
      ),
      n("7", "condition", "Check: Subscriber purchased since quiz?", 1480, {
        conditionDetails: {
          conditions: [{ variable: "total_orders", operator: ">", value: "0" }],
        },
      }),
      smsNode(
        "8",
        "Attentive: Send follow-up SMS with remaining quiz picks",
        1720,
        "{subscriber_phone}",
        "Still thinking about your {result_category} results? Here are 2 more picks we chose for you: {recommended_products[1].name} and {recommended_products[2].name}. Don't miss out -- shop now!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "When a subscriber completes a product quiz, AI extracts personalized results and recommended products. High-confidence matches get a specific product SMS; lower-confidence subscribers get a category link. After 3 days, non-purchasers receive a follow-up SMS featuring additional quiz-matched products to drive conversion.",
  },

  // ─── 90. Wishlist Alert SMS | Attentive | Pattern D ────────────────────

  "Wishlist Alert SMS": {
    nodes: [
      trigger("Attentive: Wishlist item event webhook", "webhook_app", {
        source: "attentive",
        event: "wishlist.item_updated",
        howItStarts:
          "Fires when a wishlisted item has a price drop, comes back in stock, or drops below a low-inventory threshold, triggering an SMS alert.",
        setup:
          "Connect Attentive to your product catalog webhook. Configure wishlist.item_updated events to include subscriber phone, product details, and change type (price_drop, back_in_stock, low_inventory).",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract product details, change type, and urgency level",
        280,
        "You are a wishlist alert analyst. Parse the webhook and extract: subscriber_phone, product_name, product_url, original_price, current_price, change_type (price_drop | back_in_stock | low_inventory), stock_remaining (number or null), discount_percent (number or null), and urgency_level (high, medium, low based on stock and discount magnitude).",
        "Parse this wishlist item update event: {webhook_payload}",
        [
          { field: "subscriber_phone", as: "subscriber_phone", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_url", as: "product_url", type: "string" },
          { field: "original_price", as: "original_price", type: "string" },
          { field: "current_price", as: "current_price", type: "string" },
          { field: "change_type", as: "change_type", type: "string" },
          { field: "stock_remaining", as: "stock_remaining", type: "number" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Attentive: Verify subscriber consent and preferences",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.attentivemobile.com/v1/subscribers/{subscriber_phone}",
            headers: [
              { key: "Authorization", value: "Bearer {attentive_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "subscriber.smsConsent",
                customName: "sms_consent",
                type: "boolean",
              },
              {
                name: "subscriber.wishlistAlertsPref",
                customName: "wishlist_alerts_enabled",
                type: "boolean",
              },
              {
                name: "subscriber.lastAlertDate",
                customName: "last_alert_date",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Check: High urgency (low stock < 10 or discount > 20%)?",
        760,
        {
          conditionDetails: {
            conditions: [
              { variable: "urgency_level", operator: "==", value: "high" },
            ],
          },
        },
      ),
      smsNode(
        "5",
        "Attentive: Send urgent wishlist alert with scarcity",
        1000,
        "{subscriber_phone}",
        "ALERT: {product_name} on your wishlist just dropped to {current_price} (was {original_price})! Only {stock_remaining} left. Grab yours before it's gone: {product_url}",
      ),
      smsNode(
        "5b",
        "Attentive: Send standard wishlist update SMS",
        1000,
        "{subscriber_phone}",
        "Good news! {product_name} on your wishlist has an update -- now {current_price} (was {original_price}). Check it out: {product_url}",
        400,
      ),
      n("6", "delayAction", "Wait 2 days for purchase activity", 1240, {
        delayDetails: { duration: 2, unit: "days" },
      }),
      n(
        "7",
        "condition",
        "Check: Item still available and not purchased?",
        1480,
        {
          conditionDetails: {
            conditions: [
              { variable: "stock_remaining", operator: ">", value: "0" },
            ],
          },
        },
      ),
      smsNode(
        "8",
        "Attentive: Send last-chance wishlist reminder SMS",
        1720,
        "{subscriber_phone}",
        "Last chance! {product_name} is still on your wishlist at {current_price}. Stock is running low -- don't let it slip away: {product_url}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "When a wishlisted item has a price drop, restocks, or hits low inventory, AI parses the event details and urgency. High-urgency changes (deep discounts or very low stock) trigger a scarcity-driven SMS; standard updates get a friendly notification. After 2 days, if the item is still available and unpurchased, a last-chance reminder SMS drives final conversion.",
  },

  // ─── 91. Holiday Campaign Warm-Up | Attentive | Pattern I ──────────────

  "Holiday Campaign Warm-Up": {
    nodes: [
      trigger("Attentive: Holiday campaign schedule trigger", "schedule", {
        schedule:
          "Runs 14 days before major holiday sale events (Black Friday, Valentine's Day, etc.) on a configured schedule",
        howItStarts:
          "Fires on a scheduled date 14 days before a major holiday sale to begin warming up SMS subscribers with teaser content, early-access hints, and engagement-building messages.",
        setup:
          "Configure scheduled trigger for each holiday campaign start date. Ensure Attentive subscriber segments are tagged with holiday_opt_in and purchase history attributes.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Determine holiday context, teaser angle, and subscriber segments",
        280,
        "You are a holiday campaign strategist. Given the holiday name and date, determine: holiday_name, days_until_sale, teaser_angle (e.g. sneak peek, early access, countdown), recommended_segments (array of subscriber segments to target), content_theme (festive, urgency, exclusivity), and personalization_hooks (array of data points to use).",
        "Plan teaser content for the upcoming holiday campaign. Holiday: {holiday_name}. Sale date: {sale_date}. Current date: {current_date}. Subscriber base size: {subscriber_count}.",
        [
          { field: "holiday_name", as: "holiday_name", type: "string" },
          { field: "days_until_sale", as: "days_until_sale", type: "number" },
          { field: "teaser_angle", as: "teaser_angle", type: "string" },
          { field: "content_theme", as: "content_theme", type: "string" },
          {
            field: "recommended_segments",
            as: "recommended_segments",
            type: "object",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Attentive: Lookup subscriber engagement and past holiday spend",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.attentivemobile.com/v1/segments/{segment_id}/subscribers?includeAttributes=true",
            headers: [
              { key: "Authorization", value: "Bearer {attentive_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "subscribers[0].attributes.lastHolidaySpend",
                customName: "last_holiday_spend",
                type: "number",
              },
              {
                name: "subscribers[0].attributes.engagementScore",
                customName: "engagement_score",
                type: "number",
              },
              {
                name: "subscribers[0].phone",
                customName: "subscriber_phone",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Check: Engaged subscriber (engagement score >= 50)?",
        760,
        {
          conditionDetails: {
            conditions: [
              { variable: "engagement_score", operator: ">=", value: "50" },
            ],
          },
        },
      ),
      smsNode(
        "5",
        "Attentive: Send VIP early-access teaser SMS",
        1000,
        "{subscriber_phone}",
        "Psst... our {holiday_name} sale is coming in {days_until_sale} days and YOU get early access! Reply YES to lock in your VIP spot for exclusive deals before anyone else.",
      ),
      smsNode(
        "5b",
        "Attentive: Send general holiday teaser SMS",
        1000,
        "{subscriber_phone}",
        "Mark your calendar! Our biggest {holiday_name} sale drops in {days_until_sale} days. Stay tuned for sneak peeks and exclusive SMS-only deals coming your way!",
        400,
      ),
      n(
        "6",
        "delayAction",
        "Wait 5 days for engagement and opt-in responses",
        1240,
        {
          delayDetails: { duration: 5, unit: "days" },
        },
      ),
      n(
        "7",
        "condition",
        "Check: Subscriber replied or clicked teaser?",
        1480,
        {
          conditionDetails: {
            conditions: [
              { variable: "engagement_score", operator: ">=", value: "60" },
            ],
          },
        },
      ),
      smsNode(
        "8",
        "Attentive: Send countdown SMS with sale preview and early link",
        1720,
        "{subscriber_phone}",
        "{days_until_sale} days to go! Here's a sneak peek at what's coming: up to 40% off bestsellers. Bookmark your favorites now so you're ready to shop: https://shop.example.com/{holiday_name}-preview",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Scheduled 14 days before a major holiday sale, this workflow warms up SMS subscribers. AI determines teaser angles and segments. Engaged subscribers get VIP early-access teasers; others get general hype. After 5 days, engaged responders receive a countdown SMS with sale preview links to maximize day-one revenue.",
  },

  // ─── 92. High-Intent Site Visitor SMS | Attentive | Pattern K ──────────

  "High-Intent Site Visitor SMS": {
    nodes: [
      trigger("Attentive: High-intent page view webhook", "webhook_app", {
        source: "attentive",
        event: "page.high_intent_view",
        howItStarts:
          "Fires when a known SMS subscriber views a high-intent page (pricing, checkout, product comparison) multiple times within a session, signaling strong purchase intent.",
        setup:
          "Configure Attentive identity resolution with site tracking. Set up high-intent page rules for pricing pages, size guides, FAQ, and comparison pages. Webhook fires when 2+ high-intent pages are viewed in a single session.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "Attentive: Enrich subscriber with browsing context and profile",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.attentivemobile.com/v1/subscribers/{subscriber_phone}/activity?type=page_view&limit=10",
            headers: [
              { key: "Authorization", value: "Bearer {attentive_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "activities[0].page",
                customName: "last_viewed_page",
                type: "string",
              },
              {
                name: "activities[0].productName",
                customName: "viewed_product",
                type: "string",
              },
              {
                name: "activities[0].productPrice",
                customName: "product_price",
                type: "string",
              },
              {
                name: "subscriber.phone",
                customName: "subscriber_phone",
                type: "string",
              },
              {
                name: "subscriber.attributes.totalOrders",
                customName: "total_orders",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "runCode",
        "Score: Compute intent score from page views, recency, and history",
        520,
        {
          runCodeDetails: {
            code: `const pageWeight = {"pricing": 30, "checkout": 25, "comparison": 20, "size-guide": 15, "faq": 10};
const pageScore = pageWeight[last_viewed_page] || 10;
const recencyBonus = 20;
const historyBonus = total_orders > 0 ? 15 : 0;
const intent_score = Math.min(pageScore + recencyBonus + historyBonus, 100);
const score_tier = intent_score >= 60 ? "hot" : "warm";
return { intent_score, score_tier };`,
            outputVariables: [
              { name: "intent_score", type: "number" },
              { name: "score_tier", type: "string" },
            ],
          },
        },
      ),
      n("4", "condition", "Check: Hot intent (score >= 60)?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "score_tier", operator: "==", value: "hot" },
          ],
        },
      }),
      smsNode(
        "5",
        "Attentive: Send high-intent personalized offer SMS",
        1000,
        "{subscriber_phone}",
        "We noticed you checking out {viewed_product} ({product_price}). Need help deciding? Here's 10% off to make it easier -- use code DECIDE10 at checkout: https://shop.example.com/cart",
      ),
      smsNode(
        "5b",
        "Attentive: Send warm-intent helpful nudge SMS",
        1000,
        "{subscriber_phone}",
        "Still browsing? We've got answers to your questions about {viewed_product}. Chat with us or check out our buying guide: https://shop.example.com/guide/{viewed_product}",
        400,
      ),
      n("6", "delayAction", "Wait 4 hours SLA for purchase check", 1240, {
        delayDetails: { duration: 4, unit: "hours" },
      }),
      n("7", "hitApi", "Attentive: Update subscriber intent score tag", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.attentivemobile.com/v1/subscribers/{subscriber_phone}/attributes",
          headers: [
            { key: "Authorization", value: "Bearer {attentive_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "attributes",
              value:
                '{"intent_score": "{intent_score}", "last_intent_product": "{viewed_product}", "intent_date": "{current_date}"}',
            },
          ],
          variables: [
            {
              name: "subscriber.id",
              customName: "subscriber_updated",
              type: "string",
            },
          ],
        },
      }),
      sheetsNode(
        "8",
        "Google Sheets: Log high-intent visitor for sales review",
        1720,
        "HighIntentVisitors",
        '[["{subscriber_phone}", "{viewed_product}", "{intent_score}", "{score_tier}", "{product_price}", "{current_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "When a known subscriber browses high-intent pages (pricing, checkout, comparisons), the system enriches their profile and computes a behavioral intent score. Hot-intent visitors get a personalized discount SMS; warm visitors receive a helpful nudge. After 4 hours, the subscriber's intent score is updated in Attentive and logged to a Google Sheet for sales team review.",
  },

  // ─── 93. SMS-to-Email Upgrade Prompt | Attentive | Pattern A ───────────

  "SMS-to-Email Upgrade Prompt": {
    nodes: [
      trigger("Attentive: SMS subscriber activity webhook", "webhook_app", {
        source: "attentive",
        event: "subscriber.sms_engaged",
        howItStarts:
          "Fires when an SMS-only subscriber reaches an engagement threshold (3+ link clicks in 30 days) without having an email address on file, indicating they are a prime candidate for email channel upgrade.",
        setup:
          "Configure Attentive engagement rules to fire when SMS-only subscribers (no email on file) hit 3+ link clicks within 30 days. Webhook includes subscriber phone and engagement metrics.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Analyze subscriber engagement pattern and recommend upgrade angle",
        280,
        "You are a channel strategy analyst. Given an SMS subscriber's engagement data, determine: subscriber_phone, click_count, favorite_category (most clicked product category), engagement_tier (highly_engaged, moderately_engaged), recommended_incentive (percentage discount or free shipping), and email_value_prop (why email is valuable for this subscriber based on their interests).",
        "Analyze this SMS subscriber's engagement for email upgrade eligibility: {webhook_payload}",
        [
          { field: "subscriber_phone", as: "subscriber_phone", type: "string" },
          { field: "click_count", as: "click_count", type: "number" },
          {
            field: "favorite_category",
            as: "favorite_category",
            type: "string",
          },
          { field: "engagement_tier", as: "engagement_tier", type: "string" },
          {
            field: "recommended_incentive",
            as: "recommended_incentive",
            type: "string",
          },
          { field: "email_value_prop", as: "email_value_prop", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Attentive: Lookup subscriber profile and verify no email on file",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.attentivemobile.com/v1/subscribers/{subscriber_phone}",
            headers: [
              { key: "Authorization", value: "Bearer {attentive_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "subscriber.email",
                customName: "existing_email",
                type: "string",
              },
              {
                name: "subscriber.firstName",
                customName: "first_name",
                type: "string",
              },
              {
                name: "subscriber.attributes.totalOrders",
                customName: "total_orders",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Check: Existing customer (has placed orders)?",
        760,
        {
          conditionDetails: {
            conditions: [
              { variable: "total_orders", operator: ">", value: "0" },
            ],
          },
        },
      ),
      smsNode(
        "5",
        "Attentive: Send customer-tier email upgrade SMS with incentive",
        1000,
        "{subscriber_phone}",
        "Hey {first_name}! You're one of our top SMS shoppers. Want even more {favorite_category} deals? Add your email for exclusive lookbooks + {recommended_incentive} off your next order: https://shop.example.com/email-signup?phone={subscriber_phone}",
      ),
      smsNode(
        "5b",
        "Attentive: Send prospect-tier email upgrade SMS with value prop",
        1000,
        "{subscriber_phone}",
        "Love our {favorite_category} picks? Get deeper style guides, early access sales, and more by adding your email. It takes 10 seconds: https://shop.example.com/email-signup?phone={subscriber_phone}",
        400,
      ),
      n("6", "delayAction", "Wait 2 days for email signup", 1240, {
        delayDetails: { duration: 2, unit: "days" },
      }),
      n("7", "condition", "Check: Email still not provided?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "existing_email", operator: "==", value: "" },
          ],
        },
      }),
      smsNode(
        "8",
        "Attentive: Send final email upgrade reminder SMS",
        1720,
        "{subscriber_phone}",
        "Quick reminder -- add your email to unlock exclusive {favorite_category} content and your {recommended_incentive} reward. Only takes a sec: https://shop.example.com/email-signup?phone={subscriber_phone}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "When an engaged SMS-only subscriber hits 3+ link clicks without an email on file, AI analyzes their interests and recommends a personalized upgrade angle. Existing customers receive an incentive-driven email signup prompt; prospects get a value-prop approach. After 2 days, subscribers who still haven't added email get a final reminder SMS to drive dual-channel enrollment.",
  },

  // ─── 94. Concierge Handoff from SMS | Attentive | Pattern J ────────────

  "Concierge Handoff from SMS": {
    nodes: [
      trigger("Attentive: VIP engagement signal webhook", "webhook_app", {
        source: "attentive",
        event: "subscriber.vip_engagement",
        howItStarts:
          "Fires when a VIP-tagged subscriber sends an SMS reply indicating interest in premium products, mentions a large order, or has accumulated high lifetime value, triggering a personal concierge handoff.",
        setup:
          "Configure Attentive to detect VIP engagement signals: subscriber replies mentioning 'help', 'custom', 'bulk', or 'gift' keywords, OR subscriber LTV exceeds $2,000. Webhook includes subscriber details and trigger context.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "Attentive: Lookup VIP subscriber profile and purchase history",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.attentivemobile.com/v1/subscribers/{subscriber_phone}/attributes",
            headers: [
              { key: "Authorization", value: "Bearer {attentive_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "attributes.lifetimeValue",
                customName: "ltv",
                type: "number",
              },
              {
                name: "attributes.totalOrders",
                customName: "total_orders",
                type: "number",
              },
              {
                name: "attributes.favoriteCategory",
                customName: "favorite_category",
                type: "string",
              },
              {
                name: "attributes.firstName",
                customName: "first_name",
                type: "string",
              },
              {
                name: "subscriber.phone",
                customName: "subscriber_phone",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "condition",
        "Check: Eligible for concierge (LTV >= $2000 or keyword match)?",
        520,
        {
          conditionDetails: {
            conditions: [{ variable: "ltv", operator: ">=", value: "2000" }],
          },
        },
      ),
      smsNode(
        "4",
        "Attentive: Send concierge intro SMS to VIP subscriber",
        760,
        "{subscriber_phone}",
        "Hi {first_name}! As one of our top customers, you've been matched with a personal shopping concierge. I'm Sarah, and I'll help you find exactly what you need. What are you looking for today?",
      ),
      n(
        "5",
        "delayAction",
        "Wait 5 days for concierge interaction outcome",
        1000,
        {
          delayDetails: { duration: 5, unit: "days" },
        },
      ),
      n(
        "6",
        "condition",
        "Check: Concierge interaction led to purchase?",
        1240,
        {
          conditionDetails: {
            conditions: [
              { variable: "total_orders", operator: ">", value: "0" },
            ],
          },
        },
      ),
      smsNode(
        "7",
        "Attentive: Send concierge follow-up or alternate offer",
        1480,
        "{subscriber_phone}",
        "Hi {first_name}, it's Sarah again! I wanted to follow up -- I've curated a few {favorite_category} picks based on our chat. Want me to send them over? Just reply YES!",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log concierge handoff outcome",
        1720,
        "ConciergeHandoffs",
        '[["{subscriber_phone}", "{first_name}", "{ltv}", "{total_orders}", "{favorite_category}", "handoff_completed", "{current_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "8", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_2"),
      e("6", "8", "output_1"),
      e("7", "8"),
    ],
    summary:
      "When a VIP subscriber signals high-intent via SMS keywords or exceeds $2,000 LTV, the system verifies their eligibility and introduces a personal shopping concierge via SMS. After 5 days, if no purchase resulted, the concierge sends a curated follow-up. All handoff outcomes are logged to Google Sheets for advocacy tracking and team performance review.",
  },

  // ─── 95. Post-Purchase Cross-Sell SMS | Attentive | Pattern E ──────────

  "Post-Purchase Cross-Sell SMS": {
    nodes: [
      trigger("Attentive: Order placed webhook", "webhook_app", {
        source: "attentive",
        event: "order.placed",
        howItStarts:
          "Fires when a subscriber places an order, triggering a post-purchase SMS sequence that thanks them and recommends complementary products based on their purchase.",
        setup:
          "Connect Attentive to your e-commerce platform order events. Configure order.placed webhook to include subscriber phone, order items, order total, and order ID.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract order details and generate cross-sell recommendations",
        280,
        "You are an e-commerce cross-sell strategist. Parse the order payload and extract: subscriber_phone, order_id, order_total, primary_product (most expensive item), product_category, and generate cross_sell_products (array of 3 complementary product objects with name, price, url, and reason for recommendation). Also determine: is_first_purchase (boolean), care_tip (one product care tip for the primary item).",
        "Analyze this order and recommend cross-sells: {webhook_payload}",
        [
          { field: "subscriber_phone", as: "subscriber_phone", type: "string" },
          { field: "order_id", as: "order_id", type: "string" },
          { field: "order_total", as: "order_total", type: "string" },
          { field: "primary_product", as: "primary_product", type: "string" },
          { field: "product_category", as: "product_category", type: "string" },
          {
            field: "cross_sell_products",
            as: "cross_sell_products",
            type: "object",
          },
          {
            field: "is_first_purchase",
            as: "is_first_purchase",
            type: "boolean",
          },
          { field: "care_tip", as: "care_tip", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Attentive: Lookup subscriber tenure and VIP status",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.attentivemobile.com/v1/subscribers/{subscriber_phone}/attributes",
            headers: [
              { key: "Authorization", value: "Bearer {attentive_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "attributes.firstName",
                customName: "first_name",
                type: "string",
              },
              {
                name: "attributes.vipTier",
                customName: "vip_tier",
                type: "string",
              },
              {
                name: "attributes.totalOrders",
                customName: "total_orders",
                type: "number",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Check: First purchase?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "is_first_purchase", operator: "==", value: "true" },
          ],
        },
      }),
      smsNode(
        "5",
        "Attentive: Send first-purchase thank-you SMS with care tip",
        1000,
        "{subscriber_phone}",
        "Welcome to the family, {first_name}! Your {primary_product} is on its way (Order #{order_id}). Pro tip: {care_tip}. We're so excited you chose us!",
      ),
      smsNode(
        "5b",
        "Attentive: Send returning-customer thank-you SMS",
        1000,
        "{subscriber_phone}",
        "Thanks again, {first_name}! Order #{order_id} for {primary_product} is confirmed. As a {vip_tier} member, you're the best!",
        400,
      ),
      n("6", "delayAction", "Wait 7 days for product delivery", 1240, {
        delayDetails: { duration: 7, unit: "days" },
      }),
      n("7", "condition", "Check: Product should be delivered by now?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "total_orders", operator: ">=", value: "1" },
          ],
        },
      }),
      smsNode(
        "8",
        "Attentive: Send cross-sell SMS with complementary products",
        1720,
        "{subscriber_phone}",
        "Loving your {primary_product}? Complete the look with {cross_sell_products[0].name} ({cross_sell_products[0].price}) -- {cross_sell_products[0].reason}. Shop now: {cross_sell_products[0].url}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "After a purchase, AI extracts order details and generates complementary product recommendations. First-time buyers get a welcome thank-you SMS with a care tip; returning customers get a loyalty-flavored confirmation. After 7 days (post-delivery), a cross-sell SMS featuring the top complementary product drives repeat purchases.",
  },

  // ─── 96. New Lead Welcome Nurture | Marketo | Pattern A ────────────────

  "New Lead Welcome Nurture": {
    nodes: [
      trigger("Marketo: New lead form submission webhook", "webhook_form", {
        source: "marketo",
        event: "form.submitted",
        howItStarts:
          "Fires when a new B2B lead submits a gated content form, demo request, or newsletter signup form in Marketo, starting a welcome nurture sequence with Salesforce integration.",
        setup:
          "Configure Marketo form webhook to fire on submission. Ensure form captures email, first name, company, job title, and content asset requested. Connect Salesforce for lead creation.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract lead details, company context, and content interest",
        280,
        "You are a B2B lead qualification analyst. Parse the form submission and extract: lead_email, first_name, company_name, job_title, content_requested (asset they downloaded), industry (inferred from company), company_size_estimate (SMB, Mid-Market, Enterprise based on company name), and persona_category (executive, practitioner, technical).",
        "Parse this Marketo form submission for lead qualification: {webhook_payload}",
        [
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          {
            field: "content_requested",
            as: "content_requested",
            type: "string",
          },
          { field: "industry", as: "industry", type: "string" },
          {
            field: "company_size_estimate",
            as: "company_size_estimate",
            type: "string",
          },
          { field: "persona_category", as: "persona_category", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Salesforce: Create lead and check for existing account match",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://your-instance.salesforce.com/services/data/v58.0/sobjects/Lead",
            headers: [
              { key: "Authorization", value: "Bearer {sf_access_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "Email", value: "{lead_email}" },
              { key: "FirstName", value: "{first_name}" },
              { key: "Company", value: "{company_name}" },
              { key: "Title", value: "{job_title}" },
              {
                key: "LeadSource",
                value: "Marketo Form - {content_requested}",
              },
              { key: "Industry", value: "{industry}" },
            ],
            variables: [
              { name: "id", customName: "sf_lead_id", type: "string" },
              { name: "success", customName: "sf_created", type: "boolean" },
            ],
          },
        },
      ),
      n("4", "condition", "Check: Enterprise-size company?", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "company_size_estimate",
              operator: "==",
              value: "Enterprise",
            },
          ],
        },
      }),
      emailNode(
        "5",
        "Marketo: Send enterprise welcome email with dedicated rep intro",
        1000,
        "{lead_email}",
        "Welcome to {company_name}'s journey with us, {first_name}",
        "Hi {first_name},\n\nThank you for downloading {content_requested}. As a leader at {company_name}, I wanted to personally introduce myself as your dedicated account resource.\n\nHere's what enterprise teams like yours typically find most valuable:\n- Custom deployment guide for {industry}\n- ROI calculator tailored to {company_size_estimate} organizations\n- Priority access to our solutions architect\n\nI'd love to learn more about your goals. Would a 15-minute call this week work?\n\nBest regards,\nYour Enterprise Team",
      ),
      emailNode(
        "5b",
        "Marketo: Send standard welcome email with content journey",
        1000,
        "{lead_email}",
        "Welcome, {first_name}! Here's your {content_requested}",
        "Hi {first_name},\n\nThanks for your interest in {content_requested}! Here's your download link and a few resources to get you started:\n\n1. Your requested content: {content_requested}\n2. Getting started guide for {industry} teams\n3. Customer success story: How {industry} companies drive results\n\nWe'll send you helpful resources over the next few weeks based on your interests.\n\nCheers,\nThe Team",
        400,
      ),
      n("6", "delayAction", "Wait 3 days for content engagement", 1240, {
        delayDetails: { duration: 3, unit: "days" },
      }),
      n("7", "condition", "Check: Lead opened welcome email?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "sf_created", operator: "==", value: "true" },
          ],
        },
      }),
      emailNode(
        "8",
        "Marketo: Send follow-up education email with next resource",
        1720,
        "{lead_email}",
        "Next up for {persona_category}s in {industry}, {first_name}",
        "Hi {first_name},\n\nSince you explored {content_requested}, here's the natural next step for {persona_category}s in {industry}:\n\n- Deep dive: Advanced strategies for {company_size_estimate} teams\n- Case study: How similar {industry} organizations achieved 3x ROI\n- Interactive tool: Assess your current maturity level\n\nWhich resonates most? Click through and we'll tailor your journey accordingly.\n\nBest,\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "When a new B2B lead submits a Marketo form, AI extracts qualification details and a Salesforce lead record is created. Enterprise leads receive a personalized welcome with dedicated rep introduction; others get a standard content-journey welcome. After 3 days, engaged leads receive progressive education content matched to their persona and industry.",
  },

  // ─── 97. Lifecycle Stage Nurture Stream | Marketo | Pattern I ──────────

  "Lifecycle Stage Nurture Stream": {
    nodes: [
      trigger("Marketo: Lifecycle stage change webhook", "webhook_app", {
        source: "marketo",
        event: "lead.lifecycle_stage_changed",
        howItStarts:
          "Fires when a lead's lifecycle stage changes in Marketo (e.g., Subscriber to MQL, MQL to SQL, SQL to Opportunity), triggering stage-appropriate nurture content aligned with their buying journey.",
        setup:
          "Configure Marketo webhook to fire on lifecycle stage field changes. Ensure Salesforce sync is active for stage updates. Map stage values: subscriber, lead, mql, sql, opportunity, customer.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Analyze stage transition and recommend nurture content",
        280,
        "You are a B2B lifecycle marketing strategist. Given a lead's stage transition, determine: lead_email, first_name, company_name, previous_stage, new_stage, stage_content_theme (what content is appropriate for this stage), recommended_cta (next desired action), urgency (high/medium/low based on stage velocity), and content_assets (array of 3 recommended content pieces with title, type, and url).",
        "Analyze this lifecycle stage change and recommend nurture content: {webhook_payload}",
        [
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "previous_stage", as: "previous_stage", type: "string" },
          { field: "new_stage", as: "new_stage", type: "string" },
          {
            field: "stage_content_theme",
            as: "stage_content_theme",
            type: "string",
          },
          { field: "recommended_cta", as: "recommended_cta", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Salesforce: Lookup lead engagement history and opportunity data",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://your-instance.salesforce.com/services/data/v58.0/query?q=SELECT+Id,LeadScore__c,LastActivityDate,Owner.Name+FROM+Lead+WHERE+Email='{lead_email}'",
            headers: [
              { key: "Authorization", value: "Bearer {sf_access_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "records[0].LeadScore__c",
                customName: "lead_score",
                type: "number",
              },
              {
                name: "records[0].LastActivityDate",
                customName: "last_activity_date",
                type: "string",
              },
              {
                name: "records[0].Owner.Name",
                customName: "lead_owner",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Check: High-velocity progression (MQL or SQL stage)?",
        760,
        {
          conditionDetails: {
            conditions: [
              { variable: "new_stage", operator: "==", value: "mql" },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Marketo: Send MQL-stage evaluation content email",
        1000,
        "{lead_email}",
        "{first_name}, you're ready for the next level: {stage_content_theme}",
        "Hi {first_name},\n\nYour engagement tells us you're seriously evaluating solutions for {company_name}. Here's what teams at your stage find most valuable:\n\n1. Buyer's Guide: Complete evaluation framework for {stage_content_theme}\n2. ROI Calculator: Model your expected return\n3. Comparison Matrix: See how solutions stack up\n\nYour account resource {lead_owner} is available to answer any questions.\n\n{recommended_cta}\n\nBest,\nThe Team",
      ),
      emailNode(
        "5b",
        "Marketo: Send early-stage educational nurture email",
        1000,
        "{lead_email}",
        "Explore {stage_content_theme}, {first_name}",
        "Hi {first_name},\n\nWelcome to the next chapter of your journey. Based on your interests, we've curated these resources on {stage_content_theme}:\n\n- Industry report: Latest trends affecting {company_name}\n- Best practices guide: Getting started with {stage_content_theme}\n- Expert webinar: On-demand insights from industry leaders\n\nExplore at your pace -- we'll be here when you're ready for more.\n\nCheers,\nThe Team",
        400,
      ),
      n("6", "delayAction", "Wait 5 days for content engagement", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n(
        "7",
        "condition",
        "Check: Lead engaged with stage content (score increased)?",
        1480,
        {
          conditionDetails: {
            conditions: [
              { variable: "lead_score", operator: ">=", value: "50" },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Marketo: Send progressive content with stronger CTA",
        1720,
        "{lead_email}",
        "Your next step: {recommended_cta}, {first_name}",
        "Hi {first_name},\n\nYou've been exploring {stage_content_theme} content, and we think you're ready for the next step.\n\nHere's what {company_name}-sized organizations do next:\n- Schedule a solution walkthrough with {lead_owner}\n- Access our interactive product tour\n- Review a custom business case template\n\n{recommended_cta} -- book time here: https://meetings.example.com/{lead_owner}\n\nLooking forward to connecting,\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "When a lead's lifecycle stage changes in Marketo, AI determines the appropriate content theme and CTA for the new stage. MQL-stage leads receive evaluation-focused content (buyer guides, ROI calculators); earlier-stage leads get educational nurture. After 5 days, engaged leads are pushed toward a stronger conversion action like scheduling a call with their assigned rep.",
  },

  // ─── 98. Product Interest Nurture Stream | Marketo | Pattern I ─────────

  "Product Interest Nurture Stream": {
    nodes: [
      trigger("Marketo: Product page visit webhook", "webhook_app", {
        source: "marketo",
        event: "web.product_page_visited",
        howItStarts:
          "Fires when a known Marketo lead visits a specific product or solution page 2+ times, indicating strong product interest and triggering a product-specific nurture stream.",
        setup:
          "Configure Marketo web tracking to detect repeat product page visits by known leads. Webhook fires when the same lead visits any product/solution page 2+ times within 7 days. Include lead email, product page URL, and visit count.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Identify product interest and map to nurture content",
        280,
        "You are a B2B product marketing analyst. Given a lead's product page visit data, extract: lead_email, first_name, company_name, product_name (specific product/solution they viewed), product_category, visit_count, use_case_hypothesis (why they might be interested based on product + company), and content_sequence (array of 3 content pieces: datasheet, case study, demo/trial link).",
        "Analyze this product page visit pattern and recommend nurture content: {webhook_payload}",
        [
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_category", as: "product_category", type: "string" },
          { field: "visit_count", as: "visit_count", type: "number" },
          {
            field: "use_case_hypothesis",
            as: "use_case_hypothesis",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Salesforce: Lookup lead status and existing opportunity data",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://your-instance.salesforce.com/services/data/v58.0/query?q=SELECT+Id,Status,LeadScore__c,Owner.Name,ConvertedOpportunityId+FROM+Lead+WHERE+Email='{lead_email}'",
            headers: [
              { key: "Authorization", value: "Bearer {sf_access_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "records[0].Status",
                customName: "lead_status",
                type: "string",
              },
              {
                name: "records[0].LeadScore__c",
                customName: "lead_score",
                type: "number",
              },
              {
                name: "records[0].Owner.Name",
                customName: "lead_owner",
                type: "string",
              },
              {
                name: "records[0].ConvertedOpportunityId",
                customName: "opp_id",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Check: Active opportunity already exists?", 760, {
        conditionDetails: {
          conditions: [{ variable: "opp_id", operator: "!=", value: "" }],
        },
      }),
      emailNode(
        "5",
        "Marketo: Send opportunity-aware product deep-dive email",
        1000,
        "{lead_email}",
        "{first_name}, deeper insights on {product_name} for {company_name}",
        "Hi {first_name},\n\nI see you've been exploring {product_name} -- great choice for {use_case_hypothesis}.\n\nSince you're already in conversation with {lead_owner}, here are resources to accelerate your evaluation:\n\n1. Technical architecture overview for {product_category}\n2. {company_name}-specific deployment scenario\n3. Live product environment access (request via {lead_owner})\n\nLet {lead_owner} know if you'd like a custom walkthrough.\n\nBest,\nProduct Team",
      ),
      emailNode(
        "5b",
        "Marketo: Send product introduction nurture email",
        1000,
        "{lead_email}",
        "Exploring {product_name}, {first_name}? Here's everything you need",
        "Hi {first_name},\n\nWe noticed your interest in {product_name} and wanted to share the essentials:\n\n- Product datasheet: Features, specifications, and pricing overview\n- Case study: How companies like {company_name} use {product_name} for {use_case_hypothesis}\n- Interactive demo: See {product_name} in action (5 min)\n\nCurious about how {product_name} fits your needs? Reply and we'll connect you with a specialist.\n\nCheers,\nThe Product Team",
        400,
      ),
      n("6", "delayAction", "Wait 4 days for content engagement", 1240, {
        delayDetails: { duration: 4, unit: "days" },
      }),
      n(
        "7",
        "condition",
        "Check: Lead revisited product page or engaged with email?",
        1480,
        {
          conditionDetails: {
            conditions: [
              { variable: "visit_count", operator: ">=", value: "3" },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Marketo: Send demo-push email with social proof",
        1720,
        "{lead_email}",
        "See {product_name} solve {use_case_hypothesis} -- live demo, {first_name}",
        "Hi {first_name},\n\nYour continued interest in {product_name} tells us you're serious about {use_case_hypothesis} at {company_name}.\n\nHere's what teams who took the next step discovered:\n- 40% faster time-to-value vs. alternatives\n- 92% customer satisfaction in {product_category}\n- Average 3.2x ROI within 12 months\n\nBook a 20-minute personalized demo: https://meetings.example.com/product-demo?product={product_name}&lead={lead_email}\n\nLooking forward to showing you what's possible,\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "When a known lead repeatedly visits a product page, AI identifies their product interest and hypothesizes the use case. Leads with existing opportunities receive an accelerated deep-dive email routed through their rep; new leads get an introductory product nurture. After 4 days, highly engaged leads receive a social-proof-driven demo push to convert interest into pipeline.",
  },

  // ─── 99. Industry Nurture Stream | Marketo | Pattern I ─────────────────

  "Industry Nurture Stream": {
    nodes: [
      trigger(
        "Marketo: Industry identified on lead record webhook",
        "webhook_app",
        {
          source: "marketo",
          event: "lead.industry_identified",
          howItStarts:
            "Fires when a lead's industry field is populated or updated in Marketo (via form fill, enrichment, or Salesforce sync), enabling industry-specific nurture content that speaks to vertical challenges and use cases.",
          setup:
            "Configure Marketo webhook to fire when the Industry field changes from blank to a value or is updated. Ensure Salesforce industry mapping is consistent. Standard industries: Healthcare, Financial Services, Technology, Manufacturing, Retail, Education.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Map industry to content themes, pain points, and case studies",
        280,
        "You are a B2B vertical marketing strategist. Given a lead's industry, determine: lead_email, first_name, company_name, job_title, industry, top_pain_points (array of 3 industry-specific challenges), industry_trend (current hot topic in this vertical), case_study_company (a relevant customer example in same industry), and compliance_considerations (any regulatory context for this industry).",
        "Map this lead's industry to nurture content themes: {webhook_payload}",
        [
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "industry", as: "industry", type: "string" },
          { field: "top_pain_points", as: "top_pain_points", type: "object" },
          { field: "industry_trend", as: "industry_trend", type: "string" },
          {
            field: "case_study_company",
            as: "case_study_company",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Salesforce: Lookup lead engagement and account intelligence",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://your-instance.salesforce.com/services/data/v58.0/query?q=SELECT+Id,LeadScore__c,Status,NumberOfEmployees,Owner.Name+FROM+Lead+WHERE+Email='{lead_email}'",
            headers: [
              { key: "Authorization", value: "Bearer {sf_access_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "records[0].LeadScore__c",
                customName: "lead_score",
                type: "number",
              },
              {
                name: "records[0].Status",
                customName: "lead_status",
                type: "string",
              },
              {
                name: "records[0].NumberOfEmployees",
                customName: "company_size",
                type: "number",
              },
              {
                name: "records[0].Owner.Name",
                customName: "lead_owner",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Check: Engaged lead (score >= 30)?", 760, {
        conditionDetails: {
          conditions: [{ variable: "lead_score", operator: ">=", value: "30" }],
        },
      }),
      emailNode(
        "5",
        "Marketo: Send engaged-lead industry deep-dive with case study",
        1000,
        "{lead_email}",
        "{first_name}, how {case_study_company} solved {industry}'s biggest challenge",
        "Hi {first_name},\n\nAs a {job_title} in {industry}, you're likely facing these challenges:\n\n1. {top_pain_points[0]}\n2. {top_pain_points[1]}\n3. {top_pain_points[2]}\n\nHere's how {case_study_company} (a {industry} leader like {company_name}) tackled them:\n\n- Case Study: {case_study_company}'s transformation story\n- Industry Report: {industry_trend} and what it means for your team\n- Solution Brief: Purpose-built for {industry} requirements\n\nWant to discuss how this applies to {company_name}? {lead_owner} can walk you through it.\n\nBest,\nThe {industry} Solutions Team",
      ),
      emailNode(
        "5b",
        "Marketo: Send early-stage industry awareness email",
        1000,
        "{lead_email}",
        "{industry} insights for {first_name} at {company_name}",
        "Hi {first_name},\n\nWe've put together resources specifically for {industry} professionals like you:\n\n- Trend Report: {industry_trend}\n- Checklist: Top priorities for {industry} teams this quarter\n- Quick Guide: Addressing {top_pain_points[0]}\n\nThousands of {industry} organizations rely on us to stay ahead. Explore what's working for your peers.\n\nCheers,\nThe Team",
        400,
      ),
      n("6", "delayAction", "Wait 5 days for content consumption", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n(
        "7",
        "condition",
        "Check: Lead consumed industry content (score increased)?",
        1480,
        {
          conditionDetails: {
            conditions: [
              { variable: "lead_score", operator: ">=", value: "45" },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Marketo: Send industry-specific evaluation content and CTA",
        1720,
        "{lead_email}",
        "Ready to tackle {top_pain_points[0]}, {first_name}?",
        "Hi {first_name},\n\nYou've been diving into our {industry} content -- here's the next step:\n\n- ROI Model: Calculate your potential savings addressing {top_pain_points[0]}\n- {industry} Implementation Playbook: Step-by-step deployment guide\n- Peer Roundtable: Join other {industry} leaders (next session this month)\n\nReady to see how it works for {company_name}? Schedule a tailored walkthrough: https://meetings.example.com/industry-demo?vertical={industry}\n\nBest,\nThe {industry} Solutions Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "When a lead's industry is identified in Marketo, AI maps it to vertical-specific pain points, trends, and case studies. Engaged leads receive a deep-dive email with an industry case study and solution brief; earlier leads get awareness-level industry content. After 5 days, leads who consumed content get evaluation-focused material with ROI models and a demo CTA tailored to their vertical.",
  },

  // ─── 100. Persona Nurture Stream | Marketo | Pattern I ─────────────────

  "Persona Nurture Stream": {
    nodes: [
      trigger(
        "Marketo: Persona identified on lead record webhook",
        "webhook_app",
        {
          source: "marketo",
          event: "lead.persona_identified",
          howItStarts:
            "Fires when a lead's persona/role category is identified or updated in Marketo (via job title parsing, form field, or enrichment), enabling role-specific nurture content that addresses their unique priorities and decision criteria.",
          setup:
            "Configure Marketo webhook to fire when the Persona__c field is set. Map personas: Executive (VP+), Practitioner (Manager/Director), Technical (Engineer/Architect/Developer), Champion (End User/Analyst). Ensure Salesforce sync for persona field.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Map persona to messaging framework and content priorities",
        280,
        "You are a B2B persona marketing strategist. Given a lead's persona and job title, determine: lead_email, first_name, company_name, job_title, persona (Executive, Practitioner, Technical, Champion), persona_priorities (array of 3 things this persona cares about most), decision_criteria (what influences their buy decision), content_format_preference (whitepaper, demo, ROI calculator, technical docs), and messaging_tone (strategic, practical, technical, collaborative).",
        "Map this lead's persona to a nurture content strategy: {webhook_payload}",
        [
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "persona", as: "persona", type: "string" },
          {
            field: "persona_priorities",
            as: "persona_priorities",
            type: "object",
          },
          {
            field: "decision_criteria",
            as: "decision_criteria",
            type: "string",
          },
          {
            field: "content_format_preference",
            as: "content_format_preference",
            type: "string",
          },
          { field: "messaging_tone", as: "messaging_tone", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Salesforce: Lookup lead engagement score and buying committee context",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://your-instance.salesforce.com/services/data/v58.0/query?q=SELECT+Id,LeadScore__c,Status,Owner.Name,Account.Name+FROM+Lead+WHERE+Email='{lead_email}'",
            headers: [
              { key: "Authorization", value: "Bearer {sf_access_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "records[0].LeadScore__c",
                customName: "lead_score",
                type: "number",
              },
              {
                name: "records[0].Status",
                customName: "lead_status",
                type: "string",
              },
              {
                name: "records[0].Owner.Name",
                customName: "lead_owner",
                type: "string",
              },
              {
                name: "records[0].Account.Name",
                customName: "account_name",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Check: Executive or Practitioner persona (decision-maker)?",
        760,
        {
          conditionDetails: {
            conditions: [
              { variable: "persona", operator: "==", value: "Executive" },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Marketo: Send executive-focused strategic content email",
        1000,
        "{lead_email}",
        "{first_name}, strategic insights for {job_title}s driving transformation",
        "Hi {first_name},\n\nAs a {job_title} at {company_name}, your priorities are clear: {persona_priorities[0]}, {persona_priorities[1]}, and {persona_priorities[2]}.\n\nWe've curated executive-level resources that speak directly to these:\n\n1. Executive Brief: How C-suite leaders are approaching {persona_priorities[0]}\n2. ROI Framework: Quantify the business impact for {company_name}\n3. Peer Benchmarks: See how similar organizations measure success\n\nOur team works with {job_title}s across the industry -- happy to share what's working.\n\nBest regards,\n{lead_owner}",
      ),
      emailNode(
        "5b",
        "Marketo: Send technical/practitioner hands-on content email",
        1000,
        "{lead_email}",
        "{first_name}, practical resources for {job_title}s like you",
        "Hi {first_name},\n\nAs a {job_title}, you care about {persona_priorities[0]} and {decision_criteria}. Here are resources built for your role:\n\n- {content_format_preference}: Deep technical documentation and architecture guides\n- Integration Guide: How it connects to your existing stack\n- Sandbox Access: Try it hands-on (no commitment needed)\n\nQuestions? Our solutions engineers love talking shop.\n\nCheers,\nThe Technical Team",
        400,
      ),
      n("6", "delayAction", "Wait 4 days for content engagement", 1240, {
        delayDetails: { duration: 4, unit: "days" },
      }),
      n(
        "7",
        "condition",
        "Check: Lead engaged with persona content (score >= 40)?",
        1480,
        {
          conditionDetails: {
            conditions: [
              { variable: "lead_score", operator: ">=", value: "40" },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Marketo: Send persona-tailored evaluation CTA email",
        1720,
        "{lead_email}",
        "Your next step as a {persona}, {first_name}",
        "Hi {first_name},\n\nBased on your engagement, here's the recommended next step for {persona}s evaluating solutions:\n\nFor {job_title}s who prioritize {decision_criteria}:\n- Customized demo: See exactly how it addresses {persona_priorities[0]}\n- Business case template: Pre-built for {persona} stakeholders\n- Reference call: Connect with a {job_title} peer at a similar organization\n\nBook your personalized session: https://meetings.example.com/persona-demo?persona={persona}&lead={lead_email}\n\nLooking forward to connecting,\n{lead_owner}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "When a lead's persona is identified in Marketo, AI maps their role to specific priorities, decision criteria, and content preferences. Executives receive strategic content (ROI frameworks, peer benchmarks); technical/practitioner personas get hands-on resources (docs, sandbox access). After 4 days, engaged leads receive a persona-tailored evaluation CTA with customized demo booking and peer reference opportunities.",
  },
};
